/* SendGridSettingsPanel — Admin → Settings card.
 *
 * Lets the platform owner change the SendGrid API key / sender email / sender
 * name from the UI. Updates take effect immediately (no backend restart) and
 * are stored encrypted in `app_settings`. A "Send test email" button posts a
 * sample message so the admin can confirm deliverability without leaving the
 * dashboard.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Mail, Send, Check, AlertTriangle, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export const SendGridSettingsPanel = ({ api, adminEmail }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ api_key: '', sender_email: '', sender_name: '' });
  const [testTo, setTestTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/email-settings');
      setData(r.data);
      setForm({
        api_key: '',
        sender_email: r.data.sender_email || '',
        sender_name: r.data.sender_name || '',
      });
      if (!testTo && adminEmail) setTestTo(adminEmail);
    } catch (e) {
      toast.error('Failed to load SendGrid settings');
    } finally {
      setLoading(false);
    }
  }, [api, adminEmail, testTo]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.sender_email.trim()) {
      toast.error('Sender email is required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        sender_email: form.sender_email.trim(),
        sender_name: form.sender_name.trim(),
      };
      if (form.api_key.trim()) body.api_key = form.api_key.trim();
      const r = await api.put('/admin/email-settings', body);
      toast.success(r.data.message || 'SendGrid credentials saved');
      setForm((f) => ({ ...f, api_key: '' }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testTo.trim()) {
      toast.error('Enter a recipient email');
      return;
    }
    setTesting(true);
    try {
      const r = await api.post('/admin/email-settings/test', { to_email: testTo.trim() });
      if (r.data.accepted_by_sendgrid) {
        toast.success(`SendGrid accepted the test message → ${r.data.to}`);
      } else {
        toast.error(r.data.note || 'SendGrid rejected the message');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed');
    } finally {
      setTesting(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Clear the DB override and revert to environment-defined SendGrid credentials?')) return;
    setClearing(true);
    try {
      await api.put('/admin/email-settings', { api_key: '__clear__' });
      toast.success('Reverted to environment defaults');
      setForm({ api_key: '', sender_email: '', sender_name: '' });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear');
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="sendgrid-settings-card">
        <CardContent className="py-10 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
        </CardContent>
      </Card>
    );
  }

  const sourceBadge = data?.source === 'db' ? (
    <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100" data-testid="sendgrid-source-badge">
      DB override
    </Badge>
  ) : (
    <Badge className="bg-zinc-100 text-zinc-700 hover:bg-zinc-100" data-testid="sendgrid-source-badge">
      Environment
    </Badge>
  );

  return (
    <Card data-testid="sendgrid-settings-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-600" />
              SendGrid Email Configuration
            </CardTitle>
            <CardDescription>
              Manage the SendGrid API key + sender identity used for every transactional email
              (verification, password resets, ticket replies, team invites). Saved values
              activate instantly — no redeploy required.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {sourceBadge}
            <Button
              variant="ghost"
              size="icon"
              onClick={load}
              title="Refresh"
              data-testid="sendgrid-refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Current status row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-50">
            <div>
              <p className="text-sm font-medium">API key</p>
              <p className="text-xs text-zinc-500" data-testid="sendgrid-key-status">
                {data?.api_key_masked || 'Not configured'}
              </p>
            </div>
            {data?.api_key_set ? (
              <Check className="w-5 h-5 text-emerald-500" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-500" />
            )}
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-50">
            <div>
              <p className="text-sm font-medium">Sender</p>
              <p className="text-xs text-zinc-500">
                {data?.sender_name ? `${data.sender_name} <${data?.sender_email || ''}>` : (data?.sender_email || '—')}
              </p>
            </div>
            {data?.sender_email ? (
              <Check className="w-5 h-5 text-emerald-500" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-500" />
            )}
          </div>
        </div>

        {/* Edit form */}
        <div className="space-y-3 border-t border-zinc-200 pt-4">
          <div className="space-y-1.5">
            <Label htmlFor="sg-api-key">API Key</Label>
            <Input
              id="sg-api-key"
              type="password"
              placeholder={data?.api_key_set ? 'Leave blank to keep existing key' : 'SG.xxxxxxxxxxx...'}
              value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              data-testid="sendgrid-api-key-input"
              autoComplete="new-password"
            />
            <p className="text-xs text-zinc-500">
              Get one at <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">SendGrid → Settings → API Keys</a>. Needs <code>Mail Send</code> permission.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="sg-sender-email">Sender Email</Label>
              <Input
                id="sg-sender-email"
                type="email"
                placeholder="noreply@yourdomain.com"
                value={form.sender_email}
                onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
                data-testid="sendgrid-sender-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sg-sender-name">Sender Name</Label>
              <Input
                id="sg-sender-name"
                placeholder="PostToSocial"
                value={form.sender_name}
                onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
                data-testid="sendgrid-sender-name-input"
              />
            </div>
          </div>
          <p className="text-xs text-zinc-500 leading-relaxed">
            ⚠️ The sender email must be either an individually verified sender OR a domain-authenticated
            address in your SendGrid account. Unverified senders are rejected with HTTP 403.
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button onClick={handleSave} disabled={saving} data-testid="sendgrid-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save credentials
            </Button>
            {data?.db_override && (
              <Button
                variant="outline"
                onClick={handleClear}
                disabled={clearing}
                data-testid="sendgrid-clear-btn"
              >
                {clearing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Revert to env
              </Button>
            )}
          </div>
        </div>

        {/* Test send block */}
        <div className="border-t border-zinc-200 pt-4 space-y-3">
          <Label htmlFor="sg-test-to" className="text-sm font-medium">Send a test email</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id="sg-test-to"
              type="email"
              placeholder="recipient@example.com"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              className="max-w-xs"
              data-testid="sendgrid-test-recipient-input"
            />
            <Button
              onClick={handleSendTest}
              disabled={testing || !data?.api_key_set}
              variant="secondary"
              data-testid="sendgrid-send-test-btn"
            >
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send test
            </Button>
          </div>
          <p className="text-xs text-zinc-500">
            Confirms SendGrid accepts the message. Check your inbox + the{' '}
            <a href="https://app.sendgrid.com/email_activity" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
              SendGrid Activity Feed
            </a>{' '}
            for the final delivery status.
          </p>
        </div>

        {data?.updated_at && (
          <p className="text-xs text-zinc-400 pt-1">
            Last updated {new Date(data.updated_at).toLocaleString()}
            {data.updated_by_email ? ` by ${data.updated_by_email}` : ''}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default SendGridSettingsPanel;
